import type { StreamingBlock } from "@storytell/streaming";
import { getBlockContent } from "@storytell/streaming";
import { TbBrain } from "solid-icons/tb";
import { createMemo, Show } from "solid-js";
import { safeParseBlockJson } from "../message/parseBlockContent";
import { useChatRuntime } from "../runtime/ChatRuntime";

type MemoryScope = "user" | "project" | "organization";

type MemoryOperation = "create" | "update" | "noop" | "replace" | "remove";

interface MemoryData {
  type: "memory";
  /** Wire value; the resolve pipeline can report an outcome this build predates. */
  operation: string;
  /** Empty on a merge into an existing memory until the write path fills it in. */
  scope: MemoryScope | "";
  memoryId: string;
  content: string;
}

const OPERATION_LABEL_KEYS: Record<string, string> = {
  create: "memory.saved",
  update: "memory.updated",
  noop: "memory.already_remembered",
  replace: "memory.replaced",
  remove: "memory.already_remembered_removed",
} satisfies Record<MemoryOperation, string>;

/**
 * Catalog key for a write outcome. An outcome this build predates still stored
 * something, so it reads as the neutral "updated" rather than inventing copy.
 */
export function memoryLabelKey(operation: string): string {
  return OPERATION_LABEL_KEYS[operation] ?? "memory.updated";
}

interface MemoryRendererProps {
  block: StreamingBlock;
}

export function MemoryRenderer(props: MemoryRendererProps) {
  const rt = useChatRuntime();

  const memoryData = createMemo<MemoryData | null>(() => {
    if (props.block.finalContent?.type === "text" && props.block.finalContent.text) {
      const parsed = safeParseBlockJson(props.block.finalContent.text) as MemoryData | null;
      if (parsed?.type === "memory") return parsed;
    }
    const content = getBlockContent(props.block.blockId);
    if (content) {
      const parsed = safeParseBlockJson(content) as MemoryData | null;
      if (parsed?.type === "memory") return parsed;
    }
    return null;
  });

  const label = () => {
    const data = memoryData();
    if (!data) return "";
    return rt.t(memoryLabelKey(data.operation));
  };

  const preview = () => {
    const data = memoryData();
    if (!data?.content) return "";
    return data.content.length > 60 ? `${data.content.slice(0, 60)}...` : data.content;
  };

  const scope = () => memoryData()?.scope;

  const handleNavigate = () => {
    const target = scope();
    if (!target) return;
    rt.actions.openMemories?.(target);
  };

  return (
    <Show when={memoryData()}>
      <div class="flex items-center gap-2 rounded-lg bg-violet-500/10 px-4 py-3 text-sm text-secondary-text dark:bg-violet-400/10">
        <TbBrain class="size-3.5 shrink-0 text-violet-500 dark:text-violet-400" />
        <span class="truncate">
          <span class="font-medium">{label()}</span>
          <Show when={preview()}>
            <span class="text-muted-text"> &mdash; {preview()}</span>
          </Show>
        </span>
        <Show when={rt.actions.openMemories && scope()}>
          <button
            type="button"
            onClick={handleNavigate}
            class="ml-auto shrink-0 text-violet-600 hover:underline dark:text-violet-400"
          >
            {rt.t("view")}
          </button>
        </Show>
      </div>
    </Show>
  );
}
